/**
 * Centralised SEO / canonical-URL source of truth.
 *
 * WHY a dedicated module: `metadataBase` previously read `NEXTAUTH_URL`, which
 * on preview/staging deploys points at a *.vercel.app host — that silently
 * poisons every canonical, OG and sitemap URL with the wrong origin. SEO URLs
 * must always resolve to the one canonical production host, regardless of which
 * environment renders the page. `SITE_URL` lets us override per-environment,
 * but the fallback is ALWAYS the production domain so a missing env var can
 * never emit a non-canonical URL into a `<link rel="canonical">`.
 */

/** The one canonical production origin. No trailing slash. */
export const SITE_URL = (
  process.env.NEXT_PUBLIC_SITE_URL ||
  process.env.SITE_URL ||
  "https://journail.app"
).replace(/\/$/, "");

export const SITE_NAME = "Journail";

/**
 * True ONLY on the real production deployment. Vercel sets `VERCEL_ENV` to
 * "production" exclusively for the production environment; the staging custom
 * environment and PR previews report "preview" (or the custom env name), and
 * local dev has it unset. Used to gate behaviour that must NEVER run outside
 * production — search indexing (robots noindex) and outgoing email (which would
 * otherwise reach the real users mirrored into the staging Neon branch).
 */
export const IS_PRODUCTION_RUNTIME = process.env.VERCEL_ENV === "production";

/**
 * Primary market is global English (SL is a secondary/local niche), so the
 * default crawlable language is EN. The cookie-based UI still lets a visitor
 * switch to SL, but the indexable defaults below are English.
 */
export const DEFAULT_LOCALE = "en";

export const SEO_DEFAULTS = {
  title: "Journail — AI Daily Planner, Goal Tracking & Journal",
  titleTemplate: "%s — Journail",
  description:
    "Journail plans your day with AI, links tasks to your yearly, monthly and weekly goals, and keeps a journal that writes itself from what you actually did.",
  /** Short description for OG/Twitter cards where space is tight. */
  ogDescription:
    "AI daily planning and reflection for professionals and freelancers. Plan, focus, review — in one editorial journal.",
  keywords: [
    "AI daily planner",
    "daily planner and journal",
    "AI journal",
    "productivity journal",
    "AI task manager",
    "goal planning app",
    "smart daily planner",
    "AI productivity coach",
    "strategic daily planning",
    "daily alignment tool",
  ],
} as const;

/**
 * Public profiles for `Organization.sameAs` / `twitter:site`. Fill these in as
 * real accounts come online — DO NOT invent handles that don't exist, an
 * unreachable `sameAs` URL is worse than none.
 */
export const SOCIAL = {
  /** Twitter/X handle including the leading @, or null if none yet. */
  twitter: null as string | null,
  /** Verified public profile URLs for the Organization schema. */
  sameAs: [] as string[],
};

export const CONTACT_EMAIL = "info@journail.app";

/** Absolute URL helper — joins a path onto the canonical origin. */
export function absoluteUrl(path = "/"): string {
  if (path.startsWith("http")) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}
